import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Utensils, Plus, Edit, Trash2, Search, X, Image as ImageIcon, Upload, Lock, ArrowLeft } from "lucide-react";
import { getUser } from "../../auth/Auth";
import { deleteRequest, uploadFile } from "../../routes/Routes";
import { loadMenus, loadMenu } from "../../common/Common"; // ← use your new helpers

const initialForm = { name: "", price: "", description: "", file: null, imagePreview: "" };

function MmModal({ open, onClose, onSave, saving, initial, isEditing }) {
    const [form, setForm] = useState(initial || initialForm);
    useEffect(() => setForm(initial || initialForm), [initial]);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const onPickFile = (e) => {
        const file = e.target.files?.[0] || null;
        update("file", file);
        if (!file) return update("imagePreview", "");
        const reader = new FileReader();
        reader.onload = (ev) => update("imagePreview", String(ev.target?.result || ""));
        reader.readAsDataURL(file);
    };

    if (!open) return null;

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center" onClick={onClose}>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-4 shadow-lg w-100" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                    <h5 className="mb-0 fw-bold">{isEditing ? "Edit Menu Item" : "Create Menu Item"}</h5>
                    <button className="btn btn-light rounded-circle" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="p-3">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Name</label>
                            <input className="form-control" value={form.name} onChange={(e) => update("name", e.target.value)} required />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Price</label>
                            <input className="form-control" value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="e.g., LKR 3,200" required />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Description</label>
                            <textarea className="form-control" rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label d-flex align-items-center gap-2"><Upload size={16} /> Image (choose from your PC)</label>
                            <input type="file" accept="image/*" className="form-control" onChange={onPickFile} />
                            {form.imagePreview ? (
                                <div className="mt-2">
                                    <img src={form.imagePreview} alt="Preview" className="rounded border" style={{ width: 120, height: 120, objectFit: "cover" }} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 p-3 border-top">
                    <button className="btn btn-light" onClick={onClose}>Cancel</button>
                    <button className="btn gradient-secondary text-white" disabled={saving} onClick={() => onSave(form)}>
                        {saving ? "Saving…" : (isEditing ? "Save changes" : "Create")}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export default function MenuManager() {
    const [me, setMe] = useState(undefined);
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [loadingList, setLoadingList] = useState(false);
    const [banner, setBanner] = useState({ type: "", text: "" });

    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { getUser().then(setMe).catch(() => setMe(null)); }, []);
    const token = me?.token;
    const roleId = Number(me?.role_id ?? me?.role?.id ?? me?.roleId);
    const isAdmin = roleId === 1;

    const fetchList = useCallback(async () => {
        if (!token) return;
        setLoadingList(true);
        try {
            const list = await loadMenus(token);  // ← helper
            setRows(Array.isArray(list) ? list : []);
        } catch {
            setRows([]);
            setBanner({ type: "danger", text: "Failed to load menu items" });
        } finally {
            setLoadingList(false);
        }
    }, [token]);

    useEffect(() => { if (me !== undefined && token && isAdmin) fetchList(); }, [me, token, isAdmin, fetchList]);

    const filtered = rows.filter((r) => {
        const q = filter.trim().toLowerCase();
        if (!q) return true;
        return (
            String(r.name || "").toLowerCase().includes(q) ||
            String(r.price || "").toLowerCase().includes(q) ||
            String(r.description || "").toLowerCase().includes(q)
        );
    });

    const toFormData = (f) => {
        const fd = new FormData();
        fd.append("name", f.name ?? "");
        fd.append("price", f.price ?? "");
        fd.append("description", f.description ?? "");
        if (f.file) fd.append("image", f.file);
        return fd;
    };

    const createItem = async (f) => {
        if (!isAdmin) return;
        setSaving(true); setBanner({ type: "", text: "" });
        try {
            const { data, status } = await uploadFile(toFormData(f), "/api/menu", token);
            if (status === 200 && !data?.error) {
                setBanner({ type: "success", text: data?.message || "Menu item created" });
                setModalOpen(false);
                await fetchList();
            } else {
                setBanner({ type: "danger", text: data?.message || "Failed to create menu item" });
            }
        } catch {
            setBanner({ type: "danger", text: "Network error while creating menu item" });
        } finally { setSaving(false); }
    };

    const saveEdit = async (id, f) => {
        if (!isAdmin) return;
        setSaving(true); setBanner({ type: "", text: "" });
        try {
            const { data, status } = await uploadFile(toFormData(f), `/api/menu/update/${id}`, token);
            if (status === 200 && !data?.error) {
                setBanner({ type: "success", text: data?.message || "Menu item updated" });
                setEditing(null);
                await fetchList();
            } else {
                setBanner({ type: "danger", text: data?.message || "Failed to update menu item" });
            }
        } catch {
            setBanner({ type: "danger", text: "Network error while updating menu item" });
        } finally { setSaving(false); }
    };

    const deleteItem = async (id) => {
        if (!isAdmin) return;
        if (!window.confirm("Delete this menu item?")) return;
        setBanner({ type: "", text: "" });
        try {
            const { data, status } = await deleteRequest(`/api/menu/${id}`, token);
            if (status === 200 && !data?.error) {
                setBanner({ type: "success", text: data?.message || "Menu item deleted" });
                setRows((p) => p.filter((x) => String(x.id) !== String(id)));
            } else {
                setBanner({ type: "danger", text: data?.message || "Failed to delete menu item" });
            }
        } catch {
            setBanner({ type: "danger", text: "Network error while deleting menu item" });
        }
    };

    if (me === undefined) {
        return (<div className="d-flex align-items-center justify-content-center vh-100"><div className="text-center"><div className="spinner-border text-secondary" /><div className="text-muted small mt-2">Loading session…</div></div></div>);
    }

    if (!isAdmin) {
        return (
            <div className="d-flex align-items-center justify-content-center vh-100 px-3">
                <div className="bg-white border rounded-4 p-4 p-md-5 text-center shadow-sm" style={{ maxWidth: 520 }}>
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-25 mb-3" style={{ width: 64, height: 64 }}>
                        <Lock size={28} className="text-warning" />
                    </div>
                    <h2 className="h4 fw-bold mb-2">Not authorized</h2>
                    <p className="text-muted mb-4">You don’t have permission to manage the Menu.</p>
                    <a href="/portal/qrs" className="btn btn-outline-secondary rounded-3"><ArrowLeft size={16} className="me-2" />Back to Portal</a>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 p-md-5">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-3 text-white d-flex align-items-center justify-content-center gradient-secondary" style={{ width: 48, height: 48 }}>
                        <Utensils size={24} />
                    </div>
                    <div>
                        <h1 className="fs-3 fw-bold mb-0">Menu</h1>
                        <div className="text-muted small">Create, edit and manage menu items</div>
                    </div>
                </div>
                <button className="btn gradient-secondary text-white rounded-3 fw-semibold d-flex align-items-center gap-2 shadow" onClick={() => { setModalOpen(true); setEditing(null); }}>
                    <Plus size={18} /> New Item
                </button>
            </div>

            {banner.text && <div className={`alert alert-${banner.type} rounded-3 py-2 px-3`}>{banner.text}</div>}

            <div className="bg-white rounded-3 border p-3 mb-3">
                <div className="row g-2 align-items-center">
                    <div className="col-md-6">
                        <div className="position-relative">
                            <Search className="position-absolute top-50 translate-middle-y text-muted" style={{ left: 12 }} size={18} />
                            <input className="form-control ps-5" placeholder="Search by name, price or description…" value={filter} onChange={(e) => setFilter(e.target.value)} />
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <span className="text-muted small">{filtered.length} items</span>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-3 border overflow-auto">
                <table className="table align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: 56 }}>Image</th>
                            <th>Name</th>
                            <th style={{ width: 140 }}>Price</th>
                            <th>Description</th>
                            <th style={{ width: 220 }} className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingList ? (
                            <tr>
                                <td colSpan={5} className="text-center py-5">
                                    <div className="spinner-border text-secondary" />
                                    <div className="text-muted small mt-2">Loading menu...</div>
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center text-muted py-5">No items found</td>
                            </tr>
                        ) : (
                            filtered.map((r, i) => {
                                const desc = String(r.description || "");
                                const short = desc.length > 90 ? desc.slice(0, 90) + "..." : desc;
                                const imgSrc = r.image || r.image_url || "";

                                return (
                                    <tr key={r.id || i}>
                                        <td>
                                            {imgSrc ? (
                                                <img
                                                    src={imgSrc}
                                                    alt=""
                                                    className="rounded"
                                                    style={{ width: 40, height: 40, objectFit: "cover" }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded bg-light d-flex align-items-center justify-content-center"
                                                    style={{ width: 40, height: 40 }}
                                                >
                                                    <ImageIcon size={16} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="fw-semibold">{r.name || "-"}</td>
                                        <td>{r.price || "-"}</td>
                                        <td className="text-muted small">{short}</td>
                                        <td className="text-end">
                                            <div className="d-inline-flex gap-2">
                                                <button
                                                    className="btn btn-outline-primary btn-sm rounded-3 d-inline-flex align-items-center gap-2"
                                                    onClick={async () => {
                                                        try {
                                                            const v = await loadMenu(r.id, token);
                                                            const item = v || r;
                                                            setEditing({
                                                                id: r.id,
                                                                name: item.name ?? "",
                                                                price: item.price ?? "",
                                                                description: item.description ?? "",
                                                                file: null,
                                                                imagePreview: item.image || item.image_url || "",
                                                            });
                                                        } catch {
                                                            setEditing({
                                                                id: r.id,
                                                                name: r.name ?? "",
                                                                price: r.price ?? "",
                                                                description: r.description ?? "",
                                                                file: null,
                                                                imagePreview: r.image || r.image_url || "",
                                                            });
                                                        }
                                                    }}
                                                    title="Edit item"
                                                >
                                                    <Edit size={16} /> Edit
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm rounded-3 d-inline-flex align-items-center gap-2"
                                                    onClick={() => deleteItem(r.id)}
                                                    title="Delete item"
                                                >
                                                    <Trash2 size={16} /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <MmModal open={modalOpen && !editing} onClose={() => setModalOpen(false)} onSave={createItem} saving={saving} isEditing={false} />
            <MmModal open={!!editing} onClose={() => setEditing(null)} onSave={(f) => saveEdit(editing.id, f)} saving={saving} initial={editing || undefined} isEditing />
        </div>
    );
}
